/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DEFAULT_LANGUAGE, TIZEN_PLATFORM } from "../../config";
import { Log, Sage, SageIdentityGate } from "../logging";
import { MessageService } from "../infrastructure";
// Multi build support need to add for Unity
// import packageJson from "./package.alias.json"; // __PREBUILD_APP_VER__
import { TivoSettings } from "./TivoSettings";
import { TvProperties } from "./TvProperties";
import { initWebOsDeviceData, webOsPlatformBack } from "./webOsDeviceInit";
import { BrowserName, DeviceType, MessageType } from "../enums";
import {
  StorageAdapter,
  STORAGE_KEY_IDP_JWT_TOKEN,
  STORAGE_KEY_IP_JWT_TOKEN,
  STORAGE_KEY_LAST_AUTH_CONTEXT,
  STORAGE_KEY_SOFT_CLIENT_CERT,
  STORAGE_KEY_SOFT_CLIENT_CERT_KEY,
  STORAGE_KEY_LAST_TUNED_CHANNEL,
  STORAGE_KEY_DEVICE_UUID
} from "../storage";
import {
  CryptoProviderFactory,
  CryptoPrivateKey,
  CryptoPublicKey
} from "./crypto";

/**
 * Extended Window with shell app injections.
 */
interface IWindowExtended extends Window {
  __IS_VEGA_OS_PLATFORM__?: boolean;
  exitApp?: () => void;
  shellAppVersion?: string;
  parentLocalStorage?: Storage;
}

/**
 * Browser info returned by getBrowserInfo().
 */
interface IBrowserInfo {
  name: BrowserName;
  version: string;
}

/**
 * Persisted DLID identifier stored in IndexedDB.
 *
 * The DLID is a permanent, per-device UUID that never changes once generated.
 * Stored separately from the cryptographic keys so it survives key regeneration.
 */
interface TDlidIdRecord {
  /** UUID v4 device login identifier. */
  dlid: string;
}

/**
 * Persisted DLID cryptographic material stored in IndexedDB.
 *
 * Contains the RSA-2048 key pair and DLID signature used for device attestation.
 * Can be regenerated independently (e.g. on sign-in error) without changing the DLID.
 */
interface TDlidKeysRecord {
  /** RSA-2048 public key in JWK format — shared with the backend. */
  publicKey: CryptoPublicKey;
  /** RSA-2048 private key in JWK format — never leaves the device. */
  privateKey: CryptoPrivateKey;
  /** Base64url-encoded RSA-2048 signature of the DLID string, signed with the private key. */
  signature: string;
  /** UUID key identifier for the device key pair — used as `kid` in primary JWTs. */
  pid: string;
}

/**
 * Persisted soft client certificate record stored in IndexedDB.
 *
 * Contains the DER/PEM-encoded X.509 certificate, the bodyId at acquisition
 * time (for reuse validation per REQ-CERT-03), and validity timestamps
 * derived from the certificate's notBefore/notAfter or configured defaults.
 */
interface TSoftClientCertRecord {
  /** DER/PEM-encoded X.509 certificate string. */
  certificate: string;
  /** bodyId at the time the certificate was acquired — used for reuse validation. */
  bodyId: string;
  /** Certificate validity start time in milliseconds since epoch. */
  startTime: number;
  /** Certificate validity end time in milliseconds since epoch. */
  endTime: number;
}

/**
 * Persisted soft client certificate private key record stored in IndexedDB.
 *
 * Stored separately from the certificate metadata for security isolation.
 */
interface TSoftClientCertKeyRecord {
  /** PEM/DER-encoded RSA private key string from the softClientCert API response. */
  privateKey: string;
}

export class DeviceModel {
  private static readonly TAG = "DeviceModel";

  private static readonly WEBOS_PLATFORM = "webOS";

  private static readonly RSA_ALGORITHM: string = "RSASSA-PKCS1-v1_5";

  private static readonly cryptoProvider = CryptoProviderFactory.getProvider();

  /**
   * DLID time-to-live: ~10 years in milliseconds.
   * Semi-permanent because the DLID should survive across sessions.
   */
  private static readonly DLID_TTL_MS: number = 315_360_000_000;

  // TODO: Start:::::::: Multi build support need to add for Unity.
  // The `APP_HOME_PAGE` will get updated at the time of build process. During the development,
  // the `APP_HOME_PAGE` will be `/` by default.
  // NOTE: Please do not remove __PREBUILD_APP_HOME_PAGE__ tag.
  private static readonly APP_HOME_PAGE: string = "/"; // __PREBUILD_APP_HOME_PAGE__

  // The `appVersionName` will get updated at the time of build process. During the development,
  // the `appVersionName` will be `development-${packageJson.version}` by default.
  // NOTE: Please do not remove __PREBUILD_APP_VER__ tag.
  private static readonly APP_VERSION_NAME: string = `development-v0.0.0`; // __PREBUILD_APP_VER__
  // TODO: End:::::::: Multi build support need to add for Unity.

  /** Cached result of getBrowserInfo() — parsed once, reused on every call. */
  private static mBrowserInfo: IBrowserInfo | null = null;

  private static mCaDeviceId: string = "";

  /** Cached result of getDetectedDeviceType() — parsed once, reused on every call. */
  private static mDeviceType: DeviceType | null = null;

  private static mDeviceUuid: string = "";

  /**
   * Device Login ID — unique per device.
   * Persisted in IndexedDB under the key "dlid".
   */
  private static mDlid: string = "";

  /** RSA-2048 public key (JWK) associated with the current DLID. */
  private static mDlidPublicKey: CryptoPublicKey | null = null;

  /** RSA-2048 private key (JWK) associated with the current DLID. */
  private static mDlidPrivateKey: CryptoPrivateKey | null = null;

  /** Base64url-encoded RSA-2048 signature of the DLID, signed with the private key. */
  private static mDlidSignature: string = "";

  /** UUID key identifier for the device key pair — used as `kid` in primary JWTs. */
  private static mPid: string = "";

  /**
   * Serialisation guard for {@link initDlid}. Coalesces concurrent
   * calls so that only one generation + storage write occurs.
   */
  private static mDlidInitPromise: Promise<void> | null = null;

  private static mBodyId: string = "";

  private static mPartnerId: string = "";

  private static mPartnerCustomerId: string = "";

  private static mSamlToken: { samlToken: string; tokenExpirationTime: Date };

  private static mSessionId: string = "";

  private static mWanIpAddress: string = "";

  private static oAppEnvironment: string = "";

  private static oFlavour: string = "";

  private static readonly oTivoSettings: TivoSettings = new TivoSettings();

  private static readonly oTVProperties: TvProperties = new TvProperties();

  public static readonly SAGE_LOGGING_VERSION = "2.8.4";

  // ──────────────────────────────────────────────────────────────────────────
  // INITIALIZATION
  // ──────────────────────────────────────────────────────────────────────────

  public static async initialize() {
    try {
      const isLgTmis = DeviceModel.isTmisLgShell();
      const isSamsungTmis = DeviceModel.isTmisSamsungShell();
      if (isLgTmis || isSamsungTmis) {
        Log.i(
          DeviceModel.TAG,
          "initialize(): TMIS shell detected (" +
            (isLgTmis ? "LG" : "Samsung") +
            "), skipping MessageService calls."
        );
        await DeviceModel.initializeForTmisShell(isLgTmis);
      } else {
        await DeviceModel.initializeForUnityShell();
      }

      await DeviceModel.initDeviceUuid();
      DeviceModel.initSessionId();
      DeviceModel.initCaDeviceId();
      await DeviceModel.getPartnerCustomerId();
    } catch (error) {
      Log.e(
        DeviceModel.TAG,
        "initialize(): Failed to initialize device model.",
        error
      );
      throw new Error("Not a real device.");
    }
  }

  /**
   * TMIS shell initialization path (LG and Samsung).
   *
   * TMIS shells have no postMessage bridge so MessageService
   * calls are skipped entirely. TvProperties getters fall
   * back to window.deviceInfo / UA parsing. TivoSettings
   * fetches config from S3 via fetchAppConfig. Environment
   * and flavour are resolved from the URL and TivoSettings.
   *
   * @param isLg - true for LG (webOS), false for Samsung (Tizen).
   *   LG requires webOS Luna service calls to populate device data;
   *   Samsung shells pre-inject device data on window.
   */
  private static async initializeForTmisShell(
    isLg: boolean = true
  ): Promise<void> {
    if (isLg) {
      // Populate window.deviceId and window.deviceInfo via webOS Luna
      // services before TvProperties reads them.
      await initWebOsDeviceData();
    }

    await Promise.all([
      DeviceModel.oTVProperties.initialize(true),
      DeviceModel.oTivoSettings.initialize(true).catch((error: unknown) => {
        Log.w(
          DeviceModel.TAG,
          "initializeForTmisShell(): TivoSettings " +
            "config fetch failed, using defaults: " +
            String(error)
        );
      })
    ]);

    DeviceModel.oAppEnvironment = DeviceModel.detectEnvironmentFromUrl();
    Log.i(
      DeviceModel.TAG,
      "initializeForTmisShell(): Environment " +
        "resolved from URL: " +
        DeviceModel.oAppEnvironment
    );

    DeviceModel.oFlavour = DeviceModel.oTivoSettings.getMsoName() || "";
    Log.i(
      DeviceModel.TAG,
      "initializeForTmisShell(): Flavour " +
        "resolved from msoName: " +
        DeviceModel.oFlavour
    );

    const tvProps = DeviceModel.oTVProperties;
    Log.d(
      DeviceModel.TAG,
      "initializeForTmisShell(): === Resolved Values === " +
        "environment=" +
        DeviceModel.oAppEnvironment +
        ", flavour=" +
        DeviceModel.oFlavour +
        ", deviceModel=" +
        tvProps.getDeviceModel() +
        ", platformName=" +
        tvProps.getPlatformName() +
        ", platformVersion=" +
        tvProps.getPlatformVersion() +
        ", locale=" +
        tvProps.getLocale() +
        ", region=" +
        tvProps.getRegion() +
        ", shellVersion=" +
        DeviceModel.oTivoSettings.getShellVersion() +
        ", msoName=" +
        (DeviceModel.oTivoSettings.getMsoName() || "") +
        ", tsriDomain=" +
        (DeviceModel.oTivoSettings.getTsriDomain() || "")
    );
  }

  /**
   * Unity shell initialization path.
   *
   * Unity shells provide a full postMessage bridge so all
   * data is fetched via MessageService.
   */
  private static async initializeForUnityShell(): Promise<void> {
    const [, , appEnv, flavour] = await Promise.all([
      DeviceModel.oTVProperties.initialize(),
      DeviceModel.oTivoSettings.initialize(),
      new MessageService(MessageType.GET_APP_ENVIRONMENT).getResponse(),
      new MessageService(MessageType.GET_FLAVOUR).getResponse()
    ]);
    DeviceModel.oAppEnvironment = appEnv === true ? "dev" : <string>appEnv;
    DeviceModel.oFlavour = <string>flavour;
  }

  /**
   * Initialize the CA Device ID.
   * If the app is running inside an iframe, generate a CA Device ID based on the
   * HSN and MSO name. Otherwise, retrieve it from storage or generate a new one.
   */
  private static initCaDeviceId() {
    // TODO: '../storage/Storage' module does not exist yet.
    // const browserCaDeviceId = DeviceModel.generateUniqueId(
    //   `caDeviceId-${DeviceModel.getRandomCryptoSeed()}`
    // );
    // DeviceModel.mCaDeviceId =
    //   Storage.getInstance().getItem(Storage.Keys.CA_DEVICE_ID) ||
    //   browserCaDeviceId;
    // if (DeviceModel.inFrame()) {
    //   const seed = `${DeviceModel.getTVProperties().getHsn()}-${DeviceModel.getTiVoSettings().getMsoName()}`;
    //   DeviceModel.mCaDeviceId = DeviceModel.generateUniqueId(seed);
    // } else {
    //   Storage.getInstance().setItem(
    //     Storage.Keys.CA_DEVICE_ID,
    //     DeviceModel.mCaDeviceId
    //   );
    // }

    DeviceModel.mCaDeviceId = DeviceModel.generateUniqueId();
  }

  /**
   * Initialize the Device UUID.
   * The Device UUID is retrieved from storage if it exists, otherwise a new one is generated
   * based on a random cryptographic seed and stored.
   */
  private static async initDeviceUuid(): Promise<string> {
    try {
      const storage: StorageAdapter = StorageAdapter.getInstance();
      const storedDeviceUuid: string | null = await storage.get<string>(
        STORAGE_KEY_DEVICE_UUID
      );

      if (storedDeviceUuid !== null && storedDeviceUuid !== "") {
        return storedDeviceUuid;
      }

      DeviceModel.mDeviceUuid = DeviceModel.generateUniqueId();
      await storage.set<string>(
        STORAGE_KEY_DEVICE_UUID,
        DeviceModel.mDeviceUuid,
        Number.POSITIVE_INFINITY
      );
      return DeviceModel.mDeviceUuid;
    } catch (error: unknown) {
      Log.e(DeviceModel.TAG, "initDeviceUuid()", error);
      DeviceModel.mDeviceUuid = DeviceModel.generateUniqueId();
      return DeviceModel.mDeviceUuid;
    }
  }

  /**
   * Initialize the Session ID.
   * The Session ID is generated based on a random cryptographic seed.
   */
  private static initSessionId() {
    if (!DeviceModel.mSessionId) {
      DeviceModel.mSessionId = DeviceModel.generateUniqueId();
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // DLID & CRYPTOGRAPHIC IDENTITY
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Public entry point for DLID initialisation.
   *
   * Serialises concurrent calls via a Promise guard so that only one
   * generation + IndexedDB write occurs. Subsequent calls while the
   * first is in-flight will await the same Promise.
   *
   * Call this during device initialisation.
   */
  public static async initDlid(): Promise<void> {
    if (DeviceModel.mDlidInitPromise !== null) {
      return DeviceModel.mDlidInitPromise;
    }

    const promise: Promise<void> = DeviceModel.performDlidInit().finally(() => {
      DeviceModel.mDlidInitPromise = null;
    });
    DeviceModel.mDlidInitPromise = promise;

    return promise;
  }

  /**
   * Initialise the Device Login ID.
   *
   * The DLID is a globally-unique, per-device identifier persisted
   * in checksummed IndexedDB storage under the key `dlid`,
   * alongside an RSA-2048 key pair for cryptographic device attestation.
   *
   * **Security model:**
   * - The **private key** never leaves the device.
   * - The **public key** is sent to the backend during registration.
   * - The backend can challenge the device to sign a nonce with the
   *   private key and verify the signature with the stored public key.
   *
   * **Race-condition safeguards:**
   * - **In-tab:** A Promise guard ({@link mDlidInitPromise})
   *   serialises concurrent calls so only one generation + write occurs.
   * - **Cross-device:** UUID v4 via `crypto.getRandomValues()` provides
   *   ~2^122 bits of entropy; the backend MUST enforce a UNIQUE constraint
   *   as a secondary guard.
   */
  private static async performDlidInit(): Promise<void> {
    const dlidKey: string = "dlid";
    const keysKey: string = "dlid_keys";
    try {
      const storage: StorageAdapter = StorageAdapter.getInstance();

      const storedId: TDlidIdRecord | null =
        await storage.get<TDlidIdRecord>(dlidKey);
      const storedKeys: TDlidKeysRecord | null =
        await storage.get<TDlidKeysRecord>(keysKey);

      if (
        storedId !== null &&
        storedId !== undefined &&
        storedKeys !== null &&
        storedKeys !== undefined
      ) {
        DeviceModel.mDlid = storedId.dlid;
        DeviceModel.mDlidPublicKey = storedKeys.publicKey;
        DeviceModel.mDlidPrivateKey = storedKeys.privateKey;
        DeviceModel.mDlidSignature = storedKeys.signature;
        // Migrate legacy records that do not have a pid yet
        if (
          storedKeys.pid !== undefined &&
          storedKeys.pid !== null &&
          storedKeys.pid.length > 0
        ) {
          DeviceModel.mPid = storedKeys.pid;
        } else {
          const pid: string = DeviceModel.generateUniqueId();
          DeviceModel.mPid = pid;
          await storage.set<TDlidKeysRecord>(
            keysKey,
            {
              publicKey: storedKeys.publicKey,
              privateKey: storedKeys.privateKey,
              signature: storedKeys.signature,
              pid
            },
            DeviceModel.DLID_TTL_MS
          );
        }
        Log.d(
          DeviceModel.TAG,
          "performDlidInit(): DLID and keys retrieved from storage"
        );
        return;
      }

      const dlid: string =
        storedId !== null && storedId !== undefined
          ? storedId.dlid
          : DeviceModel.generateUniqueId();

      const { publicKey, privateKey } =
        await DeviceModel.cryptoProvider.generateRsaKeyPair();
      const signature: string = await DeviceModel.cryptoProvider.signDlid(
        dlid,
        privateKey
      );
      const pid: string = DeviceModel.generateUniqueId();

      if (storedId === null || storedId === undefined) {
        await storage.set<TDlidIdRecord>(
          dlidKey,
          { dlid },
          DeviceModel.DLID_TTL_MS
        );
      }

      await storage.set<TDlidKeysRecord>(
        keysKey,
        { publicKey, privateKey, signature, pid },
        DeviceModel.DLID_TTL_MS
      );

      DeviceModel.mDlid = dlid;
      DeviceModel.mDlidPublicKey = publicKey;
      DeviceModel.mDlidPrivateKey = privateKey;
      DeviceModel.mDlidSignature = signature;
      DeviceModel.mPid = pid;
      Log.i(
        DeviceModel.TAG,
        "performDlidInit(): DLID + RSA-2048 key pair + signature generated"
      );
    } catch (error: unknown) {
      Log.e(DeviceModel.TAG, "performDlidInit()", error);
      throw error;
    }
  }

  /**
   * Get the current Device Login ID.
   *
   * @returns The DLID string, or an empty string if not yet initialised.
   */
  public static getDlid(): string {
    return DeviceModel.mDlid;
  }

  /**
   * Alias for getDlid() — returns the Device Logging ID.
   * Used by DBS flow orchestration.
   *
   * @returns The DLID string.
   */
  public static getDeviceLoggingId(): string {
    return DeviceModel.getDlid();
  }

  /**
   * Get the RSA-2048 public key (JWK) associated with the current DLID.
   *
   * This key is safe to share with the backend for signature verification.
   *
   * @returns The public key in JWK format, or null if not yet initialised.
   */
  public static getDlidPublicKey(): CryptoPublicKey | null {
    return DeviceModel.mDlidPublicKey;
  }

  /**
   * Get the RSA-2048 private key (JWK) associated with the current DLID.
   *
   * **Security:** This key MUST never be transmitted over the network.
   * It is used exclusively on-device for signing challenges.
   *
   * @returns The private key in JWK format, or null if not yet initialised.
   */
  public static getDlidPrivateKey(): CryptoPrivateKey | null {
    return DeviceModel.mDlidPrivateKey;
  }

  /**
   * Get the base64url-encoded RSA-2048 signature of the current DLID.
   *
   * The DLID is signed with the device's private key during initialisation.
   * This signature can be verified by the backend using the corresponding public key.
   *
   * @returns The base64url-encoded signature, or an empty string if not yet initialised.
   */
  public static getDlidSignature(): string {
    return DeviceModel.mDlidSignature;
  }

  /**
   * Returns the device key pair identifier (PID).
   * Used as the `kid` header in primary JWTs and included in the claim JWT payload.
   *
   * @returns The PID string.
   */
  public static getPid(): string {
    return DeviceModel.mPid;
  }

  /**
   * Returns the primary credential JWT for DBS/DTS authorization.
   * This is the DLID signature which serves as the JWT credential
   * signed by the device private key.
   *
   * @returns The primary credential JWT string.
   */
  public static getPrimaryCredentialJwt(): string {
    return DeviceModel.getDlidSignature();
  }

  /**
   * Exports the device RSA-2048 public key as a SPKI DER buffer (ArrayBuffer).
   * Returns null if the key pair has not been initialised.
   */
  public static async getPublicKeySpki(): Promise<ArrayBuffer | null> {
    if (DeviceModel.mDlidPublicKey === null) {
      return null;
    }
    return await DeviceModel.cryptoProvider.getPublicKeySpki(
      DeviceModel.mDlidPublicKey
    );
  }

  /**
   * Check whether the DLID has been initialised and is ready for use.
   *
   * This is a lightweight readiness gate — no backend call is made.
   * It simply verifies that {@link initDlid} has completed and that
   * the in-memory DLID value is populated.
   *
   * **Prerequisites:** {@link initDlid} must have completed successfully.
   *
   * @returns `true` if the DLID is initialised and signed, `false` otherwise.
   */
  public static async isDlidReady(): Promise<boolean> {
    const dlid: string = DeviceModel.mDlid;

    if (dlid === "") {
      Log.w(
        DeviceModel.TAG,
        "isDlidReady(): DLID not initialised — call initDlid() first"
      );
      return false;
    }

    Log.i(DeviceModel.TAG, "isDlidReady(): DLID is initialised and signed");
    return true;
  }

  /**
   * Discards this device's identity and generates a new one: a fresh DLID,
   * RSA-2048 key pair, signature and pid.
   *
   * The recovery for DPS `410 AUTHS.DLID_PERMANENTLY_REMOVED` — the DLID was
   * reset in FE (Right-To-Be-Forgotten) and DPS rejects every request that
   * carries it, so the only way forward is a new identity and a new device
   * setup. Everything bound to the old identity goes with it:
   *
   * - the persisted `dlid` / `dlid_keys` records and their in-memory copies;
   * - the IdP and IP JWTs, which were bound to the old DLID by `/bind`;
   * - the last auth context, so a token refresh cannot try to renew a
   *   session that belongs to the removed identity;
   * - the soft client certificate, issued for the old device.
   *
   * Access tokens are **not** cleared here — `TokenManager` owns them and
   * depends on this class, so the caller clears them.
   *
   * Every consumer reads the DLID and keys from this class on demand, so the
   * new identity takes effect on the next claim / bind / provision call.
   *
   * @throws when a new key pair cannot be generated or persisted — the device
   *   then has no usable identity, which the caller must surface.
   */
  public static async resetDeviceIdentity(): Promise<void> {
    // Let an in-flight initialisation land first, so it cannot write the old
    // identity back after this has cleared it.
    if (DeviceModel.mDlidInitPromise !== null) {
      try {
        await DeviceModel.mDlidInitPromise;
      } catch (error: unknown) {
        Log.w(
          DeviceModel.TAG,
          "resetDeviceIdentity(): in-flight DLID init failed; resetting anyway",
          error
        );
      }
    }

    const storage: StorageAdapter = StorageAdapter.getInstance();
    await storage.delete("dlid");
    await storage.delete("dlid_keys");
    DeviceModel.mDlid = "";
    DeviceModel.mDlidPublicKey = null;
    DeviceModel.mDlidPrivateKey = null;
    DeviceModel.mDlidSignature = "";
    DeviceModel.mPid = "";

    await DeviceModel.clearIdpJwtToken();
    await DeviceModel.clearIpJwtToken();
    try {
      await storage.delete(STORAGE_KEY_LAST_AUTH_CONTEXT);
    } catch (error: unknown) {
      Log.e(
        DeviceModel.TAG,
        "resetDeviceIdentity(): last auth context delete failed",
        error
      );
    }
    try {
      await DeviceModel.clearSoftClientCert();
    } catch (error: unknown) {
      Log.e(
        DeviceModel.TAG,
        "resetDeviceIdentity(): soft client cert delete failed",
        error
      );
    }

    await DeviceModel.initDlid();
    Log.i(
      DeviceModel.TAG,
      "resetDeviceIdentity(): device identity regenerated"
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // AUTHENTICATION & TOKENS
  // ──────────────────────────────────────────────────────────────────────────

  // The access token is no longer read or written here. `TokenManager`
  // (src/model/auth) owns it end to end — memory cache, IndexedDB persistence
  // and refresh — so that no caller can obtain a token without its expiry
  // being checked first. The `accessTokenWithExpiry` record these methods used
  // to write survives only as a one-time migration source, read by
  // `TokenManager.restore()`.

  /**
   * Reads the persisted IDP JWT token. Returns undefined when the token is
   * absent or the storage record has expired — callers must handle the
   * undefined case by falling back to interactive sign-in.
   */
  public static async getIdpJwtToken(): Promise<string | undefined> {
    try {
      const record = await StorageAdapter.getInstance().get<{
        jwtToken: string;
        tokenExpirationTime: Date;
      }>(STORAGE_KEY_IDP_JWT_TOKEN);
      if (record === null || record === undefined) {
        return undefined;
      }
      const expirationMs: number =
        record.tokenExpirationTime instanceof Date
          ? record.tokenExpirationTime.getTime()
          : Number(record.tokenExpirationTime);
      if (expirationMs <= Date.now()) {
        return undefined;
      }
      return record.jwtToken;
    } catch (error: unknown) {
      Log.e(this.TAG, "getIdpJwtToken() storage read failed", error);
      return undefined;
    }
  }

  /**
   * Whether an interactive (user ID / password) sign-in record exists on this
   * device, whatever the stored JWT's own expiry.
   *
   * The token refresh strategy uses this as evidence only — the bound-device
   * path never sends the JWT — so an expired JWT must still count. Reading it
   * through {@link getIdpJwtToken}, which hides an expired JWT, cost a
   * credentials device its silent refresh once the JWT aged out.
   */
  public static async hasIdpJwtToken(): Promise<boolean> {
    try {
      const record = await StorageAdapter.getInstance().get<{
        jwtToken: string;
      }>(STORAGE_KEY_IDP_JWT_TOKEN);
      return (
        record !== null &&
        record !== undefined &&
        typeof record.jwtToken === "string" &&
        record.jwtToken.length > 0
      );
    } catch (error: unknown) {
      Log.e(this.TAG, "hasIdpJwtToken() storage read failed", error);
      return false;
    }
  }

  /**
   * Persists the IDP JWT token used to silently mint a fresh domain token
   * when the access token expires mid-session or on app boot.
   *
   * The record never expires in storage: it is the proof that this device
   * signed in interactively, which the refresh strategy needs for as long as
   * the session lives. It is removed only with the session
   * (`clearIdpJwtToken`, `resetDeviceIdentity`). `tokenExpirationTime` records
   * the JWT's own validity — 30 days when not supplied — and is what
   * {@link getIdpJwtToken} checks.
   */
  public static async setIdpJwtToken(
    jwtToken: string,
    tokenExpirationTime?: Date
  ): Promise<void> {
    try {
      const DEFAULT_JWT_VALIDITY_MS: number = 30 * 24 * 60 * 60 * 1000;
      const expiry: Date =
        tokenExpirationTime !== undefined && tokenExpirationTime !== null
          ? tokenExpirationTime
          : new Date(Date.now() + DEFAULT_JWT_VALIDITY_MS);
      await StorageAdapter.getInstance().set(
        STORAGE_KEY_IDP_JWT_TOKEN,
        { jwtToken, tokenExpirationTime: expiry },
        Number.POSITIVE_INFINITY
      );
    } catch (error: unknown) {
      Log.e(this.TAG, "setIdpJwtToken() storage write failed", error);
    }
  }

  /**
   * Removes the persisted IDP JWT token. Should be invoked from the
   * sign-out flow alongside the access token clear.
   */
  public static async clearIdpJwtToken(): Promise<void> {
    try {
      await StorageAdapter.getInstance().delete(STORAGE_KEY_IDP_JWT_TOKEN);
    } catch (error: unknown) {
      Log.e(this.TAG, "clearIdpJwtToken() storage remove failed", error);
    }
  }

  // --- IP Auto-Login token persistence ---

  /**
   * Reads the persisted IP auto-login JWT token. Returns undefined when
   * the token is absent or the storage record has expired.
   */
  public static async getIpJwtToken(): Promise<string | undefined> {
    try {
      const record = await StorageAdapter.getInstance().get<{
        jwtToken: string;
        tokenExpirationTime: Date;
      }>(STORAGE_KEY_IP_JWT_TOKEN);
      if (record === null || record === undefined) {
        return undefined;
      }
      const expirationMs: number =
        record.tokenExpirationTime instanceof Date
          ? record.tokenExpirationTime.getTime()
          : Number(record.tokenExpirationTime);
      if (expirationMs <= Date.now()) {
        return undefined;
      }
      return record.jwtToken;
    } catch (error: unknown) {
      Log.e(this.TAG, "getIpJwtToken() storage read failed", error);
      return undefined;
    }
  }

  /**
   * Persists the IP auto-login JWT token.
   * The TTL defaults to 1 hour when no explicit expiry is supplied
   * (the IP JWT is short-lived and re-obtainable at zero user cost).
   */
  public static async setIpJwtToken(
    jwtToken: string,
    tokenExpirationTime?: Date
  ): Promise<void> {
    try {
      const DEFAULT_TTL_MS: number = 60 * 60 * 1000;
      const expiry: Date =
        tokenExpirationTime !== undefined && tokenExpirationTime !== null
          ? tokenExpirationTime
          : new Date(Date.now() + DEFAULT_TTL_MS);
      await StorageAdapter.getInstance().set(
        STORAGE_KEY_IP_JWT_TOKEN,
        { jwtToken, tokenExpirationTime: expiry },
        expiry.getTime() - Date.now()
      );
    } catch (error: unknown) {
      Log.e(this.TAG, "setIpJwtToken() storage write failed", error);
    }
  }

  /**
   * Removes the persisted IP auto-login JWT token.
   */
  public static async clearIpJwtToken(): Promise<void> {
    try {
      await StorageAdapter.getInstance().delete(STORAGE_KEY_IP_JWT_TOKEN);
    } catch (error: unknown) {
      Log.e(this.TAG, "clearIpJwtToken() storage remove failed", error);
    }
  }

  // --- Last authentication context persistence ---

  /**
   * Reads the persisted last authentication context. Returns undefined
   * when no record exists. Used by the token refresh strategy to select the
   * correct refresh branch.
   */
  public static async getLastAuthContext(): Promise<
    | {
        authenticationType: string;
        authorizationSchemes: {
          claim: string;
          bind: string;
          domainToken: string;
        };
      }
    | undefined
  > {
    try {
      const record = await StorageAdapter.getInstance().get<{
        authenticationType: string;
        authorizationSchemes: {
          claim: string;
          bind: string;
          domainToken: string;
        };
      }>(STORAGE_KEY_LAST_AUTH_CONTEXT);
      if (record === null || record === undefined) {
        return undefined;
      }
      return record;
    } catch (error: unknown) {
      Log.e(this.TAG, "getLastAuthContext() storage read failed", error);
      return undefined;
    }
  }

  /**
   * Persists the authentication context so the token refresh strategy can
   * select the correct refresh branch on cold boot or mid-session.
   */
  public static async setLastAuthContext(context: {
    authenticationType: string;
    authorizationSchemes: {
      claim: string;
      bind: string;
      domainToken: string;
    };
  }): Promise<void> {
    try {
      // Never expires — valid until overwritten by a new sign-in or removed
      // by `resetDeviceIdentity`. The TTL must be passed explicitly: the
      // adapter's default is 24 hours, which silently dropped the refresh
      // branch a day after sign-in.
      await StorageAdapter.getInstance().set(
        STORAGE_KEY_LAST_AUTH_CONTEXT,
        context,
        Number.POSITIVE_INFINITY
      );
    } catch (error: unknown) {
      Log.e(this.TAG, "setLastAuthContext() storage write failed", error);
    }
  }

  public static getSamlToken() {
    return DeviceModel.mSamlToken
      ? DeviceModel.mSamlToken.samlToken
      : undefined;
  }

  /**
   * Returns the partner ID from the provisionDevice response.
   *
   * Reads from in-memory cache first, then falls back to IndexedDB
   * via TivoSettings.
   *
   * @returns The partner ID string (e.g. "tivo:pt.5087").
   */
  public static async getPartnerId(): Promise<string> {
    if (DeviceModel.mPartnerId !== "") {
      return DeviceModel.mPartnerId;
    }
    return DeviceModel.oTivoSettings.getMsoPartnerId();
  }

  /**
   * Returns the partner customer ID from the provisionDevice response.
   *
   * Reads from in-memory cache first, then falls back to IndexedDB
   * via TivoSettings.
   *
   * @returns The partner customer ID string.
   */
  public static async getPartnerCustomerId(): Promise<string> {
    if (DeviceModel.mPartnerCustomerId !== "") {
      return DeviceModel.mPartnerCustomerId;
    }

    const partnerCustomerId: string =
      await DeviceModel.oTivoSettings.getPartnerCustomerId();
    if (partnerCustomerId !== "") {
      DeviceModel.mPartnerCustomerId = partnerCustomerId;
    }
    return partnerCustomerId;
  }

  /**
   * Returns the account identifier used by analytics (`account_id`).
   *
   * For TiVo Service flows this maps to partnerCustomerId persisted by
   * TivoSettings (IndexedDB-backed). The cache is hydrated during
   * initialization via getPartnerCustomerId().
   */
  public static getAccountId(): string {
    return DeviceModel.mPartnerCustomerId;
  }

  public static setBodyId(bodyId: string) {
    DeviceModel.mBodyId = bodyId;
  }

  public static setPartnerId(partnerId: string): void {
    DeviceModel.mPartnerId = partnerId;
  }

  public static setPartnerCustomerId(partnerCustomerId: string): void {
    DeviceModel.mPartnerCustomerId = partnerCustomerId;
  }

  /**
   * Persist the provisionDevice response data to IndexedDB and in-memory cache.
   *
   * Stores bodyId, partnerId, and partnerCustomerId. The provDataSnapshot
   * field from the API response is intentionally excluded.
   *
   * @param bodyId - Device body ID (e.g. "tsn:A8F0A00000D496B").
   * @param partnerId - MSO partner ID (e.g. "tivo:pt.5087").
   * @param partnerCustomerId - Partner customer identifier.
   */
  public static async storeProvisionDeviceData(
    bodyId: string,
    partnerId: string,
    partnerCustomerId: string
  ): Promise<void> {
    try {
      DeviceModel.mBodyId = bodyId;
      DeviceModel.mPartnerId = partnerId;
      DeviceModel.mPartnerCustomerId = partnerCustomerId;

      const settings: TivoSettings = DeviceModel.oTivoSettings;
      await Promise.all([
        settings.setBodyId(bodyId),
        settings.setMsoPartnerId(partnerId),
        settings.setPartnerCustomerId(partnerCustomerId)
      ]);

      Log.i(
        DeviceModel.TAG,
        "storeProvisionDeviceData(): Provision data persisted"
      );

      Sage.setBodyId(bodyId);
      const numericId = Number.parseInt(partnerId.replaceAll(/\D/g, ""), 10);
      if (!Number.isNaN(numericId)) {
        Sage.setPartnerId(numericId);
      }

      // Flush any telemetry that was queued before bodyId/partnerId
      SageIdentityGate.onSageIdentityAvailable().catch(
        (flushError: unknown) => {
          Log.w(
            DeviceModel.TAG,
            "storeProvisionDeviceData(): Pending log flush failed",
            flushError
          );
        }
      );
    } catch (error: unknown) {
      Log.e(DeviceModel.TAG, "storeProvisionDeviceData()", error);
      throw error;
    }
  }

  public static getSamlTokenWithExpiry() {
    const samlToken = DeviceModel.mSamlToken;
    // TODO: '../storage/Storage' module does not exist yet.
    // if (!samlToken || !samlToken.samlToken)
    //   samlToken = Storage.getInstance().getItem(
    //     Storage.Keys.SAML_TOKEN_WITH_EXPIRY
    //   );
    const doesSamlTokenExist = samlToken && samlToken.samlToken;
    const isSamlTokenExpired =
      !samlToken ||
      !samlToken.tokenExpirationTime ||
      new Date().getTime() > samlToken.tokenExpirationTime.getTime();
    if (!doesSamlTokenExist || isSamlTokenExpired) {
      throw new Error("Saml Token expired or does not exist");
    }
    return samlToken;
  }

  public static setSamlTokenWithExpiry(
    samlToken: string,
    tokenExpirationTime: Date
  ) {
    DeviceModel.mSamlToken = { samlToken, tokenExpirationTime };
  }

  // ──────────────────────────────────────────────────────────────────────────
  // SOFT CLIENT CERTIFICATE
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Delete the cached soft client certificate and private key from IndexedDB.
   *
   * Called on certificate invalidation (SOFT_CLIENT_CERTIFICATE_EXPIRED trigger),
   * bodyId change, or when a fresh certificate must be acquired.
   */
  public static async clearSoftClientCert(): Promise<void> {
    try {
      const storage: StorageAdapter = StorageAdapter.getInstance();
      await storage.delete(STORAGE_KEY_SOFT_CLIENT_CERT);
      await storage.delete(STORAGE_KEY_SOFT_CLIENT_CERT_KEY);
      Log.d(
        DeviceModel.TAG,
        "clearSoftClientCert(): Certificate and private key deleted"
      );
    } catch (error: unknown) {
      Log.e(DeviceModel.TAG, "clearSoftClientCert()", error);
      throw error;
    }
  }

  /**
   * Retrieve the cached soft client certificate and private key from IndexedDB.
   *
   * StorageAdapter.get() automatically handles TTL expiry and checksum
   * verification. If either record is missing, expired, or corrupt,
   * this method returns null (cache miss).
   *
   * @returns The certificate, privateKey, startTime, and endTime — or null on cache miss.
   */
  public static async getSoftClientCert(): Promise<{
    certificate: string;
    privateKey: string;
    startTime: number;
    endTime: number;
  } | null> {
    try {
      const storage: StorageAdapter = StorageAdapter.getInstance();

      const certRecord: TSoftClientCertRecord | null =
        await storage.get<TSoftClientCertRecord>(STORAGE_KEY_SOFT_CLIENT_CERT);
      if (certRecord === null) {
        Log.d(
          DeviceModel.TAG,
          "getSoftClientCert(): Cache miss — certificate record not found"
        );
        return null;
      }

      const keyRecord: TSoftClientCertKeyRecord | null =
        await storage.get<TSoftClientCertKeyRecord>(
          STORAGE_KEY_SOFT_CLIENT_CERT_KEY
        );
      if (keyRecord === null) {
        Log.d(
          DeviceModel.TAG,
          "getSoftClientCert(): Cache miss — private key record not found"
        );
        return null;
      }

      return {
        certificate: certRecord.certificate,
        privateKey: keyRecord.privateKey,
        startTime: certRecord.startTime,
        endTime: certRecord.endTime
      };
    } catch (error: unknown) {
      Log.e(DeviceModel.TAG, "getSoftClientCert()", error);
      throw error;
    }
  }

  /**
   * Check whether a valid, non-expired soft client certificate exists
   * in IndexedDB and matches the current device bodyId.
   *
   * Validity is driven by StorageAdapter's built-in validTill check
   * (returns null for expired records) and a bodyId match (REQ-CERT-03).
   *
   * @returns true if a valid, body-matched certificate exists; false otherwise.
   */
  public static async isSoftClientCertValid(): Promise<boolean> {
    try {
      const storage: StorageAdapter = StorageAdapter.getInstance();

      const certRecord: TSoftClientCertRecord | null =
        await storage.get<TSoftClientCertRecord>(STORAGE_KEY_SOFT_CLIENT_CERT);
      if (certRecord === null) {
        return false;
      }

      const currentBodyId: string =
        await DeviceModel.getTiVoSettings().getBodyId();
      if (certRecord.bodyId !== currentBodyId) {
        Log.d(
          DeviceModel.TAG,
          "isSoftClientCertValid(): bodyId mismatch — stored vs current"
        );
        return false;
      }

      return true;
    } catch (error: unknown) {
      Log.e(DeviceModel.TAG, "isSoftClientCertValid()", error);
      return false;
    }
  }

  /**
   * Persist the soft client certificate and its private key to IndexedDB.
   *
   * Two separate checksummed records are written:
   * - `softClientCert`: certificate string, bodyId, startTime, endTime
   * - `softClientCertKey`: private key string
   *
   * Both records share the same TTL derived from `endTime - Date.now()`
   * so they expire together when the certificate becomes invalid.
   *
   * @param certificate - DER/PEM-encoded X.509 certificate
   * @param privateKey - PEM/DER-encoded RSA private key
   * @param startTime - Certificate validity start (ms since epoch)
   * @param endTime - Certificate validity end (ms since epoch)
   */
  public static async storeSoftClientCert(
    certificate: string,
    privateKey: string,
    startTime: number,
    endTime: number
  ): Promise<void> {
    try {
      const ttl: number = endTime - Date.now();

      if (!Number.isFinite(endTime) || ttl <= 0) {
        Log.d(
          DeviceModel.TAG,
          "storeSoftClientCert(): Invalid or expired endTime — skipping cache write"
        );
        await DeviceModel.clearSoftClientCert();
        return;
      }

      const storage: StorageAdapter = StorageAdapter.getInstance();
      const bodyId: string = await DeviceModel.getTiVoSettings().getBodyId();

      await storage.set<TSoftClientCertRecord>(
        STORAGE_KEY_SOFT_CLIENT_CERT,
        { certificate, bodyId, startTime, endTime },
        ttl
      );

      try {
        await storage.set<TSoftClientCertKeyRecord>(
          STORAGE_KEY_SOFT_CLIENT_CERT_KEY,
          { privateKey },
          ttl
        );
      } catch (keyWriteError: unknown) {
        Log.e(
          DeviceModel.TAG,
          "storeSoftClientCert(): Private key write failed — rolling back certificate record",
          keyWriteError
        );
        await storage.delete(STORAGE_KEY_SOFT_CLIENT_CERT);
        throw keyWriteError;
      }

      Log.d(
        DeviceModel.TAG,
        "storeSoftClientCert(): Certificate and private key persisted"
      );
    } catch (error: unknown) {
      Log.e(DeviceModel.TAG, "storeSoftClientCert()", error);
      throw error;
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // DEVICE IDENTITY & PROVISIONING
  // ──────────────────────────────────────────────────────────────────────────

  public static async getBodyId(): Promise<string> {
    return DeviceModel.mBodyId !== ""
      ? DeviceModel.mBodyId
      : DeviceModel.oTivoSettings.getBodyId();
  }

  public static async getCaDeviceId(): Promise<string> {
    const pcid = await DeviceModel.getPartnerCustomerId();
    if (pcid) {
      return `${pcid}_device`;
    }
    if (!DeviceModel.mCaDeviceId) {
      DeviceModel.mCaDeviceId = DeviceModel.generateUniqueId();
    }
    return DeviceModel.mCaDeviceId;
  }

  public static getDeviceUuid(): string {
    // TODO: remove hardcoded uuid
    return DeviceModel.mDeviceUuid || "14293bce-6c45-e72e-ee66-8f01bf854d6a";
  }

  /**
   * Returns the MSO partner ID used for service API calls.
   *
   * Delegates to {@link getPartnerId} which reads from in-memory cache
   * first, then falls back to IndexedDB via TivoSettings.
   *
   * @returns The MSO partner ID string.
   */
  public static async getMsoPartnerId(): Promise<string> {
    return DeviceModel.getPartnerId();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // BROWSER DETECTION
  // ──────────────────────────────────────────────────────────────────────────

  /**
  /**
   * Ordered browser detection rules. Each entry maps a UA marker to a
   * browser name and a regex that captures the version string.
   * Order matters — more specific patterns must precede generic ones.
   */
  private static readonly BROWSER_RULES: ReadonlyArray<
    [RegExp, BrowserName, RegExp]
  > = [
    [/Edge\//, BrowserName.EdgeLegacy, /Edge\/(\d+(\.\d+)*)/],
    [/SamsungBrowser\//, BrowserName.Samsung, /SamsungBrowser\/(\d+(\.\d+)*)/],
    [/UCBrowser\//, BrowserName.UCBrowser, /UCBrowser\/(\d+(\.\d+)*)/],
    [/YaBrowser\//, BrowserName.Yandex, /YaBrowser\/(\d+(\.\d+)*)/],
    [/Edg\//, BrowserName.Edge, /Edg\/(\d+(\.\d+)*)/],
    [/OPR\//, BrowserName.Opera, /OPR\/(\d+(\.\d+)*)/],
    [/CriOS\//, BrowserName.ChromeIOS, /CriOS\/(\d+(\.\d+)*)/],
    [/FxiOS\//, BrowserName.FirefoxIOS, /FxiOS\/(\d+(\.\d+)*)/],
    [/Chrome\//, BrowserName.Chrome, /Chrome\/(\d+(\.\d+)*)/],
    [/Firefox\//, BrowserName.Firefox, /Firefox\/(\d+(\.\d+)*)/],
    [/Safari\//, BrowserName.Safari, /Version\/(\d+(\.\d+)*)/]
  ];

  /**
   * Ordered firmware version detection rules. Unlike {@link BROWSER_RULES},
   * the version is always read from the browser's own UA token — including
   * for Safari, where the "Safari/" token (not "Version/") is used.
   * Order matters — OPR must precede Chrome/Safari since Opera's UA also
   * contains those tokens.
   */
  private static readonly FIRMWARE_VERSION_RULES: ReadonlyArray<
    [RegExp, RegExp]
  > = [
    [/OPR\//, /OPR\/(\d+(\.\d+)*)/],
    [/Chrome\//, /Chrome\/(\d+(\.\d+)*)/],
    [/Firefox\//, /Firefox\/(\d+(\.\d+)*)/],
    [/Safari\//, /Safari\/(\d+(\.\d+)*)/]
  ];

  /**
   * Normalizes a UA version string to exactly 3 dot-separated segments,
   * padding with "0" when shorter and truncating when longer.
   */
  private static normalizeFirmwareVersion(version: string): string {
    const parts: string[] = version.split(".");
    while (parts.length < 3) {
      parts.push("0");
    }
    return parts.slice(0, 3).join(".");
  }

  /**
   * Detect IE version from a UA string.
   * Handles both MSIE and Trident/rv: patterns.
   */
  private static detectIEVersion(ua: string): string {
    const msieMatch = /MSIE (\d+(\.\d+)*)/.exec(ua);
    if (msieMatch !== null) return msieMatch[1];
    const rvMatch = /rv:(\d+(\.\d+)*)/.exec(ua);
    if (rvMatch !== null) return rvMatch[1];
    return BrowserName.Unknown;
  }

  /**
   * Detect legacy Opera version from a UA string.
   * Prefers Version/ over Opera/ token.
   */
  private static detectOperaLegacyVersion(ua: string): string {
    const versionMatch = /Version\/(\d+(\.\d+)*)/.exec(ua);
    if (versionMatch !== null) return versionMatch[1];
    const operaMatch = /Opera\/(\d+(\.\d+)*)/.exec(ua);
    if (operaMatch !== null) return operaMatch[1];
    return BrowserName.Unknown;
  }

  private static extractMatch(ua: string, pattern: RegExp): string {
    const m = pattern.exec(ua);
    return m === null ? BrowserName.Unknown : m[1];
  }

  /**
   * Match the UA against the ordered browser detection rules table.
   */
  private static matchBrowserRule(ua: string): IBrowserInfo {
    for (let i: number = 0; i < DeviceModel.BROWSER_RULES.length; i++) {
      const [marker, name, versionPattern] = DeviceModel.BROWSER_RULES[i];
      if (marker.test(ua)) {
        return { name, version: DeviceModel.extractMatch(ua, versionPattern) };
      }
    }
    return { name: BrowserName.Unknown, version: BrowserName.Unknown };
  }

  /**
   * Detects browser type and version from the user agent string.
   * Detection order matters — more specific patterns must precede generic ones.
   *
   * Coverage:
   * - Legacy:  Internet Explorer (MSIE/Trident), Opera legacy, Edge Legacy (EdgeHTML)
   * - OEM:     Samsung Internet, UC Browser, Yandex Browser
   * - Modern:  Edge (Chromium), Opera (OPR), Chrome, Firefox, Safari
   * - iOS:     Chrome iOS (CriOS), Firefox iOS (FxiOS)
   *
   * Note: Brave Browser uses an identical UA to Chrome and can only be
   * detected asynchronously via `navigator.brave.isBrave()` — not covered here.
   *
   * @returns IBrowserInfo with detected browser name and version.
   */
  public static getBrowserInfo(): IBrowserInfo {
    if (DeviceModel.mBrowserInfo) return DeviceModel.mBrowserInfo;

    const ua = navigator.userAgent;
    let result: IBrowserInfo;

    if (/Trident\//.test(ua) || /MSIE /.test(ua)) {
      result = {
        name: BrowserName.IE,
        version: DeviceModel.detectIEVersion(ua)
      };
    } else if (/Opera\//.test(ua)) {
      result = {
        name: BrowserName.Opera,
        version: DeviceModel.detectOperaLegacyVersion(ua)
      };
    } else {
      result = DeviceModel.matchBrowserRule(ua);
    }

    DeviceModel.mBrowserInfo = result;
    return DeviceModel.mBrowserInfo;
  }

  public static getBrowserName(): string {
    return DeviceModel.getBrowserInfo().name;
  }

  public static getBrowserVersion(): string {
    return DeviceModel.getBrowserInfo().version;
  }

  /**
   * Firmware version, read from the browser's own UA version token and
   * normalized to 3 dot-separated segments (e.g. "151.0.0.0" -> "151.0.0",
   * "154.0" -> "154.0.0").
   */
  public static getFirmwareVersion(): string {
    const ua = navigator.userAgent;

    for (
      let i: number = 0;
      i < DeviceModel.FIRMWARE_VERSION_RULES.length;
      i++
    ) {
      const [marker, versionPattern] = DeviceModel.FIRMWARE_VERSION_RULES[i];
      if (marker.test(ua)) {
        const match = versionPattern.exec(ua);
        if (match !== null) {
          return DeviceModel.normalizeFirmwareVersion(match[1]);
        }
      }
    }

    return BrowserName.Unknown;
  }

  public static isChrome(): boolean {
    return DeviceModel.getBrowserInfo().name === BrowserName.Chrome;
  }

  public static isChromeIOS(): boolean {
    return DeviceModel.getBrowserInfo().name === BrowserName.ChromeIOS;
  }

  public static isEdge(): boolean {
    return DeviceModel.getBrowserInfo().name === BrowserName.Edge;
  }

  public static isFirefox(): boolean {
    return DeviceModel.getBrowserInfo().name === BrowserName.Firefox;
  }

  public static isFirefoxIOS(): boolean {
    return DeviceModel.getBrowserInfo().name === BrowserName.FirefoxIOS;
  }

  public static isIE(): boolean {
    return DeviceModel.getBrowserInfo().name === BrowserName.IE;
  }

  public static isOpera(): boolean {
    return DeviceModel.getBrowserInfo().name === BrowserName.Opera;
  }

  public static isSafari(): boolean {
    return DeviceModel.getBrowserInfo().name === BrowserName.Safari;
  }

  public static isSamsungBrowser(): boolean {
    return DeviceModel.getBrowserInfo().name === BrowserName.Samsung;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // DEVICE TYPE DETECTION
  // ──────────────────────────────────────────────────────────────────────────

  public static getDetectedDeviceType(): DeviceType {
    if (DeviceModel.mDeviceType) return DeviceModel.mDeviceType;

    DeviceModel.mDeviceType = DeviceModel.getTiVoSettings().detectDeviceType();
    return DeviceModel.mDeviceType;
  }

  public static isConsoleDevice(): boolean {
    return DeviceModel.getDetectedDeviceType() === DeviceType.Console;
  }

  public static isDesktopDevice(): boolean {
    return (
      DeviceModel.getDetectedDeviceType() === DeviceType.Chrome ||
      DeviceModel.getDetectedDeviceType() === DeviceType.Edge ||
      DeviceModel.getDetectedDeviceType() === DeviceType.Firefox ||
      DeviceModel.getDetectedDeviceType() === DeviceType.Opera ||
      DeviceModel.getDetectedDeviceType() === DeviceType.Safari
    );
  }

  public static isMobileDevice(): boolean {
    return DeviceModel.getDetectedDeviceType() === DeviceType.Mobile;
  }

  public static isTabletDevice(): boolean {
    return DeviceModel.getDetectedDeviceType() === DeviceType.Tablet;
  }

  public static isTvDevice(): boolean {
    return DeviceModel.getDetectedDeviceType() === DeviceType.TV;
  }

  /**
   * True on the platforms that have an actual cursor/pointer to hover and
   * click with: the desktop browser (mouse) and LG webOS (Magic Remote
   * pointer). False on every other TV platform (Samsung Tizen,
   * VegaOS) — those are D-pad-only, so a hover-to-reveal
   * scroll caret would either never appear or, worse, get stuck visible
   * once D-pad focus lands on it.
   */
  public static hasCaretPointerSupport(): boolean {
    return !DeviceModel.isTvDevice() || DeviceModel.isWebOsPlatform();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // PLATFORM DETECTION
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Detect environment from the current URL hostname.
   * Falls back to "production" when no pattern matches.
   */
  private static detectEnvironmentFromUrl(
    hostname: string = window.location.hostname
  ): string {
    if (hostname.includes("-integration")) {
      return "integration";
    }
    if (hostname.includes("-staging")) {
      return "staging";
    }
    return "production";
  }

  public static inFrame() {
    try {
      return (
        window.self !== window.top ||
        (window as unknown as IWindowExtended).__IS_VEGA_OS_PLATFORM__
      );
    } catch {
      return true;
    }
  }

  public static is1080P() {
    return window && window.screen ? window.screen.height === 1080 : false;
  }

  /**
   * Screen size in pixels, for consumers that need the exact dimensions
   * rather than the {@link is1080P} check — currently the TiVo+ ad-size
   * parameter.
   *
   * @returns Width and height, or zeroes when the screen is unavailable
   *   (which callers treat as "unknown" rather than substituting a guess).
   */
  public static getScreenResolution(): { width: number; height: number } {
    if (!window || !window.screen) {
      return { width: 0, height: 0 };
    }

    return { width: window.screen.width, height: window.screen.height };
  }

  public static isTizenPlatform() {
    return (
      DeviceModel.getTVProperties().getPlatformName() === TIZEN_PLATFORM ||
      /Tizen/i.test(navigator.userAgent)
    );
  }

  /**
   * True for 2021 Samsung Tizen TVs specifically — Tizen OS 6.0 (per
   * TvProperties.getOSVersion()'s platform-version token; Tizen 6.5 is the
   * 2022 model year, 7.0 is 2023, etc).
   */
  public static isSamsung2021Tv(): boolean {
    return (
      DeviceModel.isTizenPlatform() &&
      DeviceModel.getTVProperties().getOSVersion() === "Tizen-6.0"
    );
  }

  /**
   * Detect whether the app is running inside a TMIS LG shell.
   *
   * TMIS LG shells inject `shellAppVersion` and `parentLocalStorage`
   * on the iframe contentWindow. Returns true when both properties
   * are present on a webOS device.
   */
  public static isTmisLgShell(): boolean {
    const isWebOs = /Web0S|webOS/i.test(navigator.userAgent);
    if (!isWebOs) {
      Log.i(DeviceModel.TAG, "isTmisLgShell(): Not a webOS device, skipping.");
      return false;
    }

    const windowExt = window as unknown as IWindowExtended;
    if (
      windowExt.shellAppVersion !== undefined &&
      windowExt.shellAppVersion !== "" &&
      windowExt.parentLocalStorage !== undefined
    ) {
      Log.i(
        DeviceModel.TAG,
        "isTmisLgShell(): TMIS LG shell detected — " +
          "shellAppVersion=" +
          windowExt.shellAppVersion
      );
      return true;
    }

    Log.i(
      DeviceModel.TAG,
      "isTmisLgShell(): webOS device but no TMIS " +
        "shell properties — assuming Unity shell."
    );
    return false;
  }

  /**
   * Detect whether the app is running inside a TMIS Samsung shell.
   *
   * TMIS Samsung shells inject `shellAppVersion` and `parentLocalStorage`
   * on the iframe contentWindow. Returns true when both properties
   * are present on a Tizen device.
   */
  public static isTmisSamsungShell(): boolean {
    const isTizen = /Tizen/i.test(navigator.userAgent);
    if (!isTizen) {
      Log.i(
        DeviceModel.TAG,
        "isTmisSamsungShell(): Not a Tizen device, skipping."
      );
      return false;
    }

    const windowExt = window as unknown as IWindowExtended;
    if (
      windowExt.shellAppVersion !== undefined &&
      windowExt.shellAppVersion !== "" &&
      windowExt.parentLocalStorage !== undefined
    ) {
      Log.i(
        DeviceModel.TAG,
        "isTmisSamsungShell(): TMIS Samsung shell detected — " +
          "shellAppVersion=" +
          windowExt.shellAppVersion
      );
      return true;
    }

    Log.i(
      DeviceModel.TAG,
      "isTmisSamsungShell(): Tizen device but no TMIS " +
        "shell properties — assuming Unity shell."
    );
    return false;
  }

  public static isValidDevice() {
    const browserAllowed = DeviceModel.oTivoSettings.isBrowserAllowed();
    if (browserAllowed) return true;
    return (
      DeviceModel.inFrame() &&
      (DeviceModel.isWebOsPlatform() || DeviceModel.isTizenPlatform())
    );
  }

  public static isVegOsPlatform() {
    return (
      (window as unknown as IWindowExtended).__IS_VEGA_OS_PLATFORM__ === true
    );
  }

  public static isWebOsPlatform() {
    return (
      DeviceModel.getTVProperties().getPlatformName() ===
        DeviceModel.WEBOS_PLATFORM || /Web0S|webOS/i.test(navigator.userAgent)
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // APP METADATA & SETTINGS
  // ──────────────────────────────────────────────────────────────────────────

  public static getAppEnvironment() {
    return DeviceModel.oAppEnvironment || "production";
  }

  public static getAppHomePage() {
    return DeviceModel.APP_HOME_PAGE;
  }

  public static getAppName() {
    return DeviceModel.oTivoSettings.getAppName() || "CableCo5";
  }

  public static getAppVersionName() {
    return DeviceModel.APP_VERSION_NAME;
  }

  public static getFlavour() {
    return DeviceModel.oFlavour;
  }

  public static getFriendlyName(): string {
    return `${DeviceModel.oTVProperties.getPlatformName()} ${DeviceModel.oTVProperties.getDeviceModelCode()}`;
  }

  public static getGlobalParams() {
    return <JSON>(<unknown>{
      appVersionName: DeviceModel.getAppVersionName(),
      caDeviceId: DeviceModel.getCaDeviceId(),
      connection: DeviceModel.getTVProperties().getActiveConnectionType(),
      device: DeviceModel.getTVProperties().getDeviceName(),
      deviceModel: DeviceModel.getTVProperties().getDeviceModel(),
      deviceModelCode: DeviceModel.getTVProperties().getDeviceModelCode(),
      deviceTsn: DeviceModel.getTiVoSettings().getDeviceTsn(),
      deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
      localIP: DeviceModel.getTVProperties().getIpAddress(),
      loggingVersion: DeviceModel.SAGE_LOGGING_VERSION,
      managedIP: DeviceModel.mWanIpAddress,
      osVersion: DeviceModel.getTVProperties().getOSVersion(),
      platformName: DeviceModel.getTVProperties().getPlatformName(),
      sessionId: DeviceModel.mSessionId,
      shellAppVersion: DeviceModel.getTiVoSettings().getShellVersion(),
      userIdentifier: "",
      uuid: DeviceModel.getDeviceUuid()
    });
  }

  public static getTsriBaseUrl() {
    return DeviceModel.oTivoSettings.getTsriBaseUrl();
  }

  public static getTiVoSettings(): TivoSettings {
    return DeviceModel.oTivoSettings;
  }

  public static getTVProperties(): TvProperties {
    return DeviceModel.oTVProperties;
  }

  /**
   * Returns the device-selected language code.
   * Falls back to the app default when the device language
   * is unavailable or outside the supported locale list.
   */
  public static getDeviceLanguage(): string {
    const tvProperties: TvProperties = DeviceModel.getTVProperties();
    const hasSupportedLanguage: boolean = tvProperties.hasSupportedLanguage();

    if (!hasSupportedLanguage) {
      return DEFAULT_LANGUAGE;
    }

    const language: string = tvProperties.getLang().trim();
    return language.length > 0 ? language : DEFAULT_LANGUAGE;
  }

  public static getWanIpAddress(): string {
    return DeviceModel.mWanIpAddress;
  }

  public static setWanIpAddress(wanIp: string) {
    DeviceModel.mWanIpAddress = wanIp;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // CHANNEL STORAGE
  // ──────────────────────────────────────────────────────────────────────────

  public static async getLastTunedChannelNumber(): Promise<string | undefined> {
    try {
      const record = await StorageAdapter.getInstance().get<{
        channelNumber: string;
      }>(STORAGE_KEY_LAST_TUNED_CHANNEL);

      if (record === null || record === undefined) {
        return undefined;
      }

      return record.channelNumber;
    } catch (error: unknown) {
      Log.e(
        DeviceModel.TAG,
        "getLastTunedChannelNumber() storage read failed",
        error
      );
      return undefined;
    }
  }

  public static async setLastTunedChannelNumber(
    channelNumber: string
  ): Promise<void> {
    try {
      await StorageAdapter.getInstance().set(
        STORAGE_KEY_LAST_TUNED_CHANNEL,
        { channelNumber },
        Number.POSITIVE_INFINITY
      );
    } catch (error: unknown) {
      Log.e(
        DeviceModel.TAG,
        "setLastTunedChannelNumber() storage write failed",
        error
      );
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // APPLICATION LIFECYCLE
  // ──────────────────────────────────────────────────────────────────────────

  public static closeApplication() {
    const windowExt = window as unknown as IWindowExtended;
    if (typeof windowExt.exitApp === "function") {
      windowExt.exitApp();
    } else if (DeviceModel.isTmisLgShell()) {
      webOsPlatformBack();
    } else if (DeviceModel.inFrame()) {
      new MessageService(MessageType.CLOSE_APPLICATION)
        .getResponse()
        .catch((error) => {
          Log.e(this.TAG, error);
        });
    } else {
      window.close();
    }
  }

  public static launchSystemSetting() {
    new MessageService(MessageType.LAUNCH_SYSTEM_SETTING)
      .getResponse()
      .catch((error) => {
        Log.e(this.TAG, error);
      });
  }

  public static restartApplication() {
    if (DeviceModel.isTmisLgShell() || DeviceModel.isTmisSamsungShell()) {
      window.location.reload();
      return;
    }
    new MessageService(MessageType.RESTART_APPLICATION)
      .getResponse()
      .catch((error) => {
        Log.e(this.TAG, "Failed to restartApplication", error);
      });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // UTILITIES (PRIVATE)
  // ──────────────────────────────────────────────────────────────────────────

  private static generateUniqueId(): string {
    let id: string;
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant

    const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0"));

    id =
      hex.slice(0, 4).join("") +
      "-" +
      hex.slice(4, 6).join("") +
      "-" +
      hex.slice(6, 8).join("") +
      "-" +
      hex.slice(8, 10).join("") +
      "-" +
      hex.slice(10, 16).join("");

    // format the user id in below format:
    // 27767c65-f950-4509-86dc-b356848472c9
    const match = id.match(/^(\w{8})(\w{4})(\w{4})(\w{4})(\w+)$/);
    if (match) {
      id = `${match[1]}-${match[2]}-${match[3]}-${match[4]}-${match[5]}`;
    }
    return id;
  }
}
